import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormField,
  Header,
  Input,
  Modal,
  SpaceBetween,
  StatusIndicator,
  Table,
} from '@cloudscape-design/components';
import type { StatusIndicatorProps } from '@cloudscape-design/components';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  createVoiceTestCall as createVoiceTestCallApi,
  listVoiceTestCalls as listVoiceTestCallsApi,
} from '../../common/api/voiceTestCalls';
import { formatDateStringWithTime } from '../../common/formatting/dateFormat';
import type {
  PlaceReportWithContacts,
  VoiceTestCallCreateResponse,
  VoiceTestCallRead,
} from '../../common/types';

export type VoiceTestCallDashboardProps = {
  selectedReports: PlaceReportWithContacts[];
  createVoiceTestCall?: (
    reportId: number,
    targetPhoneNumber: string,
  ) => Promise<VoiceTestCallCreateResponse>;
  listVoiceTestCalls?: (reportId?: number) => Promise<VoiceTestCallRead[]>;
};

const PHONE_PATTERN = /^\+?[0-9().\-\s]{10,20}$/;
const ACTIVE_CALL_STATES = new Set(['queued', 'running', 'provider_starting', 'provider_initiated']);

function defaultCreateVoiceTestCall(reportId: number, targetPhoneNumber: string) {
  return createVoiceTestCallApi(reportId, {
    target_phone_number: targetPhoneNumber,
    request_id: crypto.randomUUID(),
    confirmation: 'AUTHORIZED_INTERNAL_TEST_CALL',
  });
}

function defaultListVoiceTestCalls(reportId?: number) {
  return listVoiceTestCallsApi({ report_id: reportId, limit: 20 });
}

export function validateVoiceTestCallPhone(phone: string): string | undefined {
  const trimmed = phone.trim();
  if (!trimmed) return 'Enter a test phone number.';
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length < 10 || digits.length > 15 || !PHONE_PATTERN.test(trimmed)) {
    return 'Enter a valid phone number.';
  }
  return undefined;
}

function statusType(call: VoiceTestCallRead): StatusIndicatorProps.Type {
  if (call.status === 'completed' || call.outcome === 'completed') return 'success';
  if (call.status === 'failed' || call.outcome === 'failed' || call.error) return 'error';
  if (ACTIVE_CALL_STATES.has(call.execution_state)) return 'in-progress';
  return 'info';
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}m ${remainder}s`;
}

function selectedReportName(report: PlaceReportWithContacts | undefined): string {
  return report?.place?.name ?? report?.uuid ?? 'Selected scorecard';
}

function hasActiveCalls(calls: VoiceTestCallRead[] | undefined): boolean {
  return Boolean(
    calls?.some(
      (call) => ACTIVE_CALL_STATES.has(call.status) || ACTIVE_CALL_STATES.has(call.execution_state),
    ),
  );
}

function useCompactHistoryTable(): boolean {
  const [compact, setCompact] = useState(() => window.innerWidth < 720);
  useEffect(() => {
    const update = () => setCompact(window.innerWidth < 720);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return compact;
}

export function VoiceTestCallDashboard({
  selectedReports,
  createVoiceTestCall = defaultCreateVoiceTestCall,
  listVoiceTestCalls = defaultListVoiceTestCalls,
}: VoiceTestCallDashboardProps) {
  const [modalVisible, setModalVisible] = useState(false);
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | undefined>();
  const [confirmed, setConfirmed] = useState(false);
  const [createdCall, setCreatedCall] = useState<VoiceTestCallCreateResponse | null>(null);
  const compactHistoryTable = useCompactHistoryTable();

  const selectedReport = selectedReports.length === 1 ? selectedReports[0] : undefined;
  const selectedReportId = selectedReport?.id;
  const canStartTestCall = selectedReports.length === 1 && typeof selectedReportId === 'number';
  const queryReportId = typeof selectedReportId === 'number' ? selectedReportId : undefined;

  const callsQuery = useQuery({
    queryKey: ['voice-test-calls', queryReportId],
    queryFn: () => listVoiceTestCalls(queryReportId),
    enabled: modalVisible,
    staleTime: 15_000,
    refetchInterval: (query) =>
      modalVisible && hasActiveCalls(query.state.data) ? 5_000 : false,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const error = validateVoiceTestCallPhone(phone);
      setPhoneError(error);
      if (error) throw new Error(error);
      if (!canStartTestCall || typeof selectedReportId !== 'number') {
        throw new Error('Select exactly one scorecard before placing a test call.');
      }
      if (!confirmed) {
        throw new Error('Confirm that you are authorized to call this test number.');
      }
      return await createVoiceTestCall(selectedReportId, phone.trim());
    },
    onSuccess: async (response) => {
      setCreatedCall(response);
      await callsQuery.refetch();
    },
  });

  useEffect(() => {
    const openModal = () => {
      if (canStartTestCall) setModalVisible(true);
    };
    window.addEventListener('rtf:start-voice-test-call', openModal);
    return () => window.removeEventListener('rtf:start-voice-test-call', openModal);
  }, [canStartTestCall]);

  useEffect(() => {
    if (!modalVisible) {
      setPhone('');
      setPhoneError(undefined);
      setConfirmed(false);
      setCreatedCall(null);
      createMutation.reset();
    }
  }, [modalVisible]);

  const phoneValidationError = validateVoiceTestCallPhone(phone);
  const primaryActionDisabled =
    !canStartTestCall ||
    createMutation.isPending ||
    Boolean(phoneValidationError) ||
    !confirmed;

  return (
    <Modal
      visible={modalVisible}
      onDismiss={createMutation.isPending ? () => undefined : () => setModalVisible(false)}
      header="Test scorecard call"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => setModalVisible(false)} disabled={createMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={createMutation.isPending}
              disabled={primaryActionDisabled}
              onClick={() => createMutation.mutate()}
            >
              Place test call
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        <div>
          <Box variant="awsui-key-label">Scorecard</Box>
          <Box>{selectedReportName(selectedReport)}</Box>
        </div>

        <FormField
          label="Test phone number"
          description="Enter a number you are authorized to test."
          errorText={phoneError}
        >
          <Input
            value={phone}
            inputMode="tel"
            autoComplete="tel"
            placeholder="+1 555 123 4567"
            disabled={createMutation.isPending}
            onBlur={() => setPhoneError(validateVoiceTestCallPhone(phone))}
            onChange={({ detail }) => {
              setPhone(detail.value);
              if (phoneError) setPhoneError(undefined);
              if (createdCall) setCreatedCall(null);
              if (confirmed) setConfirmed(false);
            }}
          />
        </FormField>

        <Checkbox
          checked={confirmed}
          disabled={createMutation.isPending || Boolean(phoneValidationError)}
          onChange={({ detail }) => setConfirmed(detail.checked)}
        >
          I’m authorized to place this test call to this number.
        </Checkbox>

        {createMutation.error && !phoneError ? (
          <Alert type="error" header="Test call failed">
            {createMutation.error instanceof Error
              ? createMutation.error.message
              : 'Unable to place the test call.'}
          </Alert>
        ) : null}
        {createdCall ? (
          <Alert type="success" header="Test call queued">
            Calling {createdCall.call.target_phone_number_redacted}
          </Alert>
        ) : null}

        <Table
          variant="embedded"
          loading={callsQuery.isPending}
          loadingText="Loading test call history"
          header={
            <Header
              variant="h3"
              actions={
                <Button
                  iconName="refresh"
                  onClick={() => callsQuery.refetch()}
                  loading={callsQuery.isFetching}
                >
                  Refresh
                </Button>
              }
            >
              Recent test calls
            </Header>
          }
          empty={
            <Box textAlign="center" color="text-body-secondary">
              No test calls yet
            </Box>
          }
          items={callsQuery.data ?? []}
          columnDisplay={[
            { id: 'created_at', visible: !compactHistoryTable },
            { id: 'target', visible: true },
            { id: 'status', visible: true },
          ]}
          columnDefinitions={[
            {
              id: 'created_at',
              header: 'Created',
              cell: (call) => formatDateStringWithTime(call.created_at),
            },
            {
              id: 'target',
              header: 'Target',
              cell: (call) => call.target_phone_number_redacted,
            },
            {
              id: 'status',
              header: 'Status',
              cell: (call) => (
                <SpaceBetween size="xxxs">
                  <StatusIndicator type={statusType(call)}>{call.status}</StatusIndicator>
                  <Box variant="small" color="text-body-secondary">
                    {call.outcome ?? call.error ?? 'Pending'} ·{' '}
                    {formatDuration(call.duration_seconds)}
                  </Box>
                </SpaceBetween>
              ),
            },
          ]}
        />
      </SpaceBetween>
    </Modal>
  );
}
